import { useContext, useMemo, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { Button, Card, Checkbox, Icon, Modal } from "../../components/design";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import ProcessPassedModal from "./ProcessPassedModal";
import TenantContext from "../../context/tenant/TenantContext";
import { haveValue } from "../../helper/common";
import "./ConsentStepModal.css";

const ConsentStepModal = ({
  isPopup = false,
  user,
  updateTenantData,
  originalUser,
  loadUserTenant,
  onClose,
  loadSharedProfiles,
  selectedLandlord,
  shareProfileWithLandlord,
  isJoinedFromLandlordLink,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const [, setCookie] = useCookies(["joinGroupOnLandingPopup"]);
  const { submitConsents } = useContext(TenantContext);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showTickScreen, setShowTickScreen] = useState(false);
  const insurance_service_status = isPopup
    ? selectedLandlord?.insurance_service_status
    : true; // before that it was based on landlord but now it is compulsary

  const consentSections = useMemo(() => {
    const sections = [];

    if (
      insurance_service_status === true &&
      (!haveValue(user?.consents?.gdpr_consent) ||
        !haveValue(user?.consents?.skor_consent))
    ) {
      sections.push({
        id: "gdprCheck",
        title: _t("rentalInsuranceEvaluation"),
        points: [
          _t("insuranceCompanyWillCheckCreditHistory"),
          _t("usedToCalculateRentDefaultRisk"),
          _t("youCanWithdrawConsentBeforeApproval"),
        ],
        checkboxLabel: _t(
          "iConsentToTheProcessingOfMyPersonalDataForRentalInsuranceSolvencyEvaluation"
        ),
        links: [
          {
            title: _t("viewFullGdprNotice"),
            url: "/pdf/consents/gropo_muto_consent_gdpr.pdf",
          },
          {
            title: _t("skorConsent"),
            url: "/pdf/consents/skor_consent.pdf",
          },
        ],
      });
    }

    return sections;
  }, [user]);

  const [consents, setConsents] = useState(
    consentSections.reduce((acc, section) => {
      acc[section.id] = false;
      return acc;
    }, {})
  );

  const handleToggle = (id) => {
    setConsents((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSubmitConsent = async () => {
    try {
      setSubmitLoading(true);

      let consentPayload = {};
      if (consents.gdprCheck) {
        consentPayload.gdpr_consent = true;
        consentPayload.skor_consent = true;
      }
      await submitConsents(consentPayload);
      if (isPopup) {
        if (haveValue(loadSharedProfiles)) {
          await loadSharedProfiles(user?._id, "full");
        }
        await shareProfileWithLandlord?.();
        await loadUserTenant?.("tenant");
        onClose();
      } else {
        // Not using below now isPopup will always be true
        let payload = {
          role: localStorage.getItem("usertype"),
        };

        if (isJoinedFromLandlordLink) {
          payload.is_edit = false;
          payload.profile_completed = true;

          setCookie("joinGroupOnLandingPopup", "1", { path: "/" });
          await updateTenantData(payload);
          navigate(`/home?welcome=true`);
        } else {
          payload.step = 10;

          await updateTenantData(payload);
        }
      }
    } catch (error) {
      console.log("Error submitting reason", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const isConsentUnchecked = !Object.values(consents).every(Boolean);

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={false}
      size="wide"
      align="start"
      title={isPopup ? _t("actionRequired") : _t("consentTitleOnboarding")}
      description={
        isPopup
          ? _t(
              "theLandlordRequiresTenantsToSignTheConsentsBelowInOrderToReviewYourProfile"
            )
          : _t(
              "becauseYouAppliedThroughALandlordUniqueInvitationLinkTheseConsentsAreRequiredToShareYourProfile"
            )
      }
      actionsLayout="end"
      actions={
        <>
          {isPopup && (
            <Button variant="secondary" size="lg" onClick={onClose}>
              {_t("cancel")}
            </Button>
          )}
          <Button
            size="lg"
            disabled={isConsentUnchecked}
            loading={submitLoading}
            onClick={() => {
              // This is not for onboarding (This is used in share profile modal as component)
              if (isPopup) {
                handleSubmitConsent();
              } else {
                // If joined from landlord link then this is the last step, else there is one more step
                if (isJoinedFromLandlordLink) {
                  setShowTickScreen(true);
                } else {
                  handleSubmitConsent();
                }
              }
            }}
          >
            {_t("continue")}
          </Button>
        </>
      }
    >
      <div className="consent-step__cards">
        {consentSections.map((section) => (
          <Card key={section.id} title={section.title}>
            <ul className="consent-step__points">
              {section.points.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>

            <Checkbox
              className={`consent-step__check ${
                consents[section.id] ? "is-checked" : ""
              }`}
              checked={consents[section.id] || false}
              onChange={() => handleToggle(section.id)}
              label={section.checkboxLabel}
              description={
                <span className="consent-step__links">
                  {section.links?.map((linkObj, index) => (
                    <a
                      key={index}
                      href={linkObj.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {linkObj.title}
                      <Icon name="link" size={14} />
                    </a>
                  ))}
                </span>
              }
            />
          </Card>
        ))}
      </div>

      <p className="consent-step__legal">
        {_t("theseConsentsAreLegallyBinding")}
      </p>

      <ProcessPassedModal
        heading={_t("onboardingSuccessful")}
        subheading={_t("yourOnboardingHasBeenSuccessfullyCompleted")}
        showPopup={showTickScreen}
        buttonText={"goHome"}
        handleClick={handleSubmitConsent}
      />
    </Modal>
  );
};

export default ConsentStepModal;
